import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faShieldAlt } from "@fortawesome/free-solid-svg-icons";

/**
 * Auditor profile fields, shared by the user list's edit modal and the
 * create/edit user page.
 *
 * `auditor_mdas` is a list of mda_code values; the pages join it back into the
 * comma separated string the API stores. Only Value for Money carries the
 * capital/all switch — every other category is all-releases and is narrowed by
 * the MDA tick list.
 */
export const AUDITOR_CATEGORIES = [
  "Value for Money",
  "Government Accounts",
  "Education",
  "Parastatals",
  "ICT",
  "Finance",
];

export const isAuditorRole = (role) => /^auditor$/i.test(String(role || "").trim());

export const isAuditorGeneralRole = (role) =>
  /^auditor(\s|_)?general$/i.test(String(role || "").trim());

const toggle = (list, value) =>
  list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

const AuditorScopeFields = ({
  role,
  category = "",
  mdas = [],
  scope = "",
  mdaOptions = [],
  onChange = () => {},
}) => {
  const [search, setSearch] = useState("");

  const shownOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return mdaOptions;
    return mdaOptions.filter((option) =>
      `${option.code} ${option.name}`.toLowerCase().includes(needle)
    );
  }, [mdaOptions, search]);

  if (!isAuditorRole(role) && !isAuditorGeneralRole(role)) return null;

  if (isAuditorGeneralRole(role)) {
    return (
      <Alert variant="info" className="mb-3">
        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
        The Auditor General reviews every MDA and every release type, so this
        account is not scoped.
      </Alert>
    );
  }

  const selectedShown = shownOptions.filter((option) => mdas.includes(option.code)).length;

  return (
    <Card border="light" className="mb-3">
      <Card.Body className="p-3">
        <h6 className="mb-3">
          <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
          Auditor scope
        </h6>

        <Row className="g-2 align-items-end">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Auditor category</Form.Label>
              <Form.Select
                value={category}
                onChange={(event) => {
                  const value = event.target.value;
                  onChange({
                    auditor_category: value,
                    // Value for Money covers every MDA and starts capital-only;
                    // every other category is narrowed by the MDA tick list.
                    auditor_mdas: value === "Value for Money" ? [] : mdas,
                    auditor_scope: value === "Value for Money" ? scope || "capital" : "all",
                  });
                }}
              >
                <option value="">Select category</option>
                {AUDITOR_CATEGORIES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {category === "Value for Money" ? (
            <Col md={6}>
              <Form.Group>
                <Form.Label>Release scope</Form.Label>
                <Form.Select
                  value={scope || "capital"}
                  onChange={(event) => onChange({ auditor_scope: event.target.value })}
                >
                  <option value="capital">Capital releases only</option>
                  <option value="all">All releases</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Value for Money covers every MDA; this switch narrows it to capital releases.
                </Form.Text>
              </Form.Group>
            </Col>
          ) : (
            <Col md={6}>
              <Form.Text className="text-muted">
                {category
                  ? "Assign the MDAs this auditor may review."
                  : "Choose a category to set this auditor's scope."}
              </Form.Text>
            </Col>
          )}
        </Row>

        {category && category !== "Value for Money" && (
          <div className="mt-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <Form.Label className="mb-0">
                MDAs <span className="text-muted small">({mdas.length} selected)</span>
              </Form.Label>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    onChange({
                      auditor_mdas: Array.from(
                        new Set([...mdas, ...shownOptions.map((option) => option.code)])
                      ),
                    })
                  }
                  disabled={!shownOptions.length || selectedShown === shownOptions.length}
                >
                  Select all shown
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => onChange({ auditor_mdas: [] })}
                  disabled={!mdas.length}
                >
                  Clear
                </Button>
              </div>
            </div>

            <div className="filter-search mb-2">
              <FontAwesomeIcon icon={faSearch} className="filter-search-icon" />
              <Form.Control
                type="text"
                placeholder="Search MDA name or code..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div
              className="border rounded p-2"
              style={{ maxHeight: 220, overflowY: "auto" }}
            >
              {shownOptions.length ? (
                shownOptions.map((option) => (
                  <Form.Check
                    key={option.code}
                    type="checkbox"
                    id={`auditor-mda-${option.code}`}
                    label={`${option.name} (${option.code})`}
                    checked={mdas.includes(option.code)}
                    onChange={() => onChange({ auditor_mdas: toggle(mdas, option.code) })}
                  />
                ))
              ) : (
                <p className="text-muted small mb-0">No MDA matches that search.</p>
              )}
            </div>

            {!mdas.length && (
              <div className="alert alert-warning py-2 mt-2 mb-0 small">
                Without at least one MDA this auditor will see no releases. Value for
                Money is the exception: it can be scoped by capital releases instead.
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AuditorScopeFields;
